import './styles.css';
import { NordApplication } from './app/NordApplication';
import { ClientVersionMonitor } from './app/ClientVersionMonitor';
import { ClientUpdateBanner } from './ui/ClientUpdateBanner';

const root = document.querySelector('#app');
if (!(root instanceof HTMLElement)) throw new Error('Nord application root is missing.');

const application = new NordApplication(root);
const buildId = document.querySelector<HTMLMetaElement>('meta[name="nord-client-build"]')?.content;
if (import.meta.env.PROD && buildId) {
  document.body.classList.add('game-shell');
  const banner = new ClientUpdateBanner();
  root.before(banner.element);
  const versions = new ClientVersionMonitor(buildId, () => {
    banner.show();
    application.notifyClientUpdate();
  });
  versions.start();
}
if (import.meta.env.VITE_NORD_PERFORMANCE_HOOK === '1') {
  Object.defineProperty(window, '__nordSetPerformanceWaypoint', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (worldX: number, worldZ: number) => application.setPerformanceWaypoint(worldX, worldZ),
  });
}
if (import.meta.env.VITE_NORD_E2E_HOOK === '1') {
  Object.defineProperty(window, '__nordTravelToSpecialVillage', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (villageId: number) => application.travelToSpecialVillageForE2e(villageId),
  });
  Object.defineProperty(window, '__nordGetE2ePetTarget', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (petName: string) => application.getE2ePetTarget(petName),
  });
  Object.defineProperty(window, '__nordGetE2ePublicCameraNobos', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2ePublicCameraNobos(),
  });
  Object.defineProperty(window, '__nordGetE2eMountTarget', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (mountName: string) => application.getE2eMountTarget(mountName),
  });
  Object.defineProperty(window, '__nordGetE2eAvatarTarget', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (playerId: number) => application.getE2eAvatarTarget(playerId),
  });
  Object.defineProperty(window, '__nordSetE2eLocalAvatarHidden', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (hidden: boolean) => application.setE2eLocalAvatarHidden(hidden),
  });
  Object.defineProperty(window, '__nordGetE2eTreatBuildingTarget', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (preferredX: number, preferredY: number) =>
      application.getE2eTreatBuildingTarget(preferredX, preferredY),
  });
  Object.defineProperty(window, '__nordGetE2eBuildingTarget', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingId: number, preferredX: number, preferredY: number) =>
      application.getE2eBuildingTarget(buildingId, preferredX, preferredY),
  });
  Object.defineProperty(window, '__nordCaptureE2eAuthoritativeBuildingMask', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingIds: readonly number[], readyResourcesOnly = false) =>
      application.captureE2eAuthoritativeBuildingMask(buildingIds, readyResourcesOnly),
  });
  Object.defineProperty(window, '__nordCaptureE2eAuthoritativeBuildingLandmarks', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingIds: readonly number[]) =>
      application.captureE2eAuthoritativeBuildingLandmarks(buildingIds),
  });
  Object.defineProperty(window, '__nordGetE2eWeatherBuildingEffects', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eWeatherBuildingEffects(),
  });
  Object.defineProperty(window, '__nordGetE2eDirectionMarkers', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eDirectionMarkers(),
  });
  Object.defineProperty(window, '__nordSelectE2eBuilding', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingId: number, preferredX: number, preferredY: number) =>
      application.selectE2eBuilding(buildingId, preferredX, preferredY),
  });
  Object.defineProperty(window, '__nordActivateE2eBuilding', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingId: number, preferredX: number, preferredY: number) =>
      application.activateE2eBuilding(buildingId, preferredX, preferredY),
  });
  Object.defineProperty(window, '__nordGetE2eBuildingEditPolicy', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (buildingType: number) => application.getE2eBuildingEditPolicy(buildingType),
  });
  Object.defineProperty(window, '__nordGetE2eBuildingPlacementCandidate', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (tileX: number, tileZ: number) =>
      application.getE2eBuildingPlacementCandidate(tileX, tileZ),
  });
  Object.defineProperty(window, '__nordGetE2eTutorialBuildingPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eTutorialBuildingPresentation(),
  });
  Object.defineProperty(window, '__nordGetE2eMovementTutorialState', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eMovementTutorialState(),
  });
  Object.defineProperty(window, '__nordGetE2eProductionTutorialState', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eProductionTutorialState(),
  });
  Object.defineProperty(window, '__nordGetE2eTerrainReconciliationState', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eTerrainReconciliationState(),
  });
  Object.defineProperty(window, '__nordGetE2eCameraPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eCameraPresentation(),
  });
  Object.defineProperty(window, '__nordGetE2eMountedAvatarPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eMountedAvatarPresentation(),
  });
  Object.defineProperty(window, '__nordGetE2eLocalMovementPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => {
      return application.getE2eLocalMovementPresentation();
    },
  });
  Object.defineProperty(window, '__nordGetE2eFootballPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: () => application.getE2eFootballPresentation(),
  });
  Object.defineProperty(window, '__nordGetE2eMoonwalkPresentation', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: (playerId?: number) => application.getE2eMoonwalkPresentation(playerId),
  });
}
void application.start();
